import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, Send, User } from 'lucide-react';
import { CustomerNote } from '../../../hooks/useCustomerNotes';

interface ConcludeNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  note: CustomerNote | null;
}

/**
 * Modal for concluding a customer note with required reason
 */
export const ConcludeNoteModal: React.FC<ConcludeNoteModalProps> = ({
  open,
  onClose,
  onSubmit,
  note,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError('O motivo da conclusão é obrigatório');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(trimmedReason);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao concluir aviso');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isSubmitting) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose, isSubmitting]);

  // Handle Ctrl+Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!open || !note) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gray-900 text-white rounded-xl shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-900 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Concluir Aviso</h2>
              <p className="text-sm text-gray-400">Informe o motivo da conclusão</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Note Preview */}
        <div className="px-4 pt-4">
          <div className="rounded-lg bg-gray-800 border-l-4 border-amber-400 pl-4 pr-3 py-3">
            <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{note.content}</p>
            <div className="mt-3 pt-2 border-t border-gray-700 flex items-center gap-1.5">
              <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-400">Criado por:</span>
              <span className="text-xs text-gray-200 font-medium">{note.created_by_name}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-4">
            <label htmlFor="conclude-reason" className="block text-sm font-semibold text-gray-200 mb-2">
              Motivo da Conclusão <span className="text-red-400">*</span>
            </label>
            <textarea
              ref={textareaRef}
              id="conclude-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Descreva o motivo pelo qual este aviso está sendo concluído..."
              rows={3}
              disabled={isSubmitting}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
            />
            <p className="mt-2 text-xs text-gray-500">
              Dica: <kbd className="px-1 py-0.5 text-[10px] bg-gray-700 border border-gray-600 rounded text-gray-400">Ctrl+Enter</kbd> para enviar
            </p>

            {error && (
              <div className="mt-3 p-3 bg-red-900 border border-red-700 rounded-lg">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !reason.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Concluindo...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Concluir Aviso
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConcludeNoteModal;
